import type Stripe from 'stripe';

import type * as api from '@gitbook/api-client';
import type { ContentRefKind } from '@gitbook/app-content-refs';
import type {
    ContentPermissionLevelOrNone,
    ContentThemeMode,
    DefaultPermissionLevel,
    GitSyncProvider,
    IntegrationInstallationSpaceSelection,
    OrganizationBillingStatus,
    OrganizationCommunityType,
    OrganizationMemberRoleOrGuest,
    OrganizationTrialDecision,
    OrganizationType,
    OrganizationUseCase,
    UserTheme,
} from '@gitbook/app-database';
import type { FeatureFlagKey } from '@gitbook/app-featureflags';
import type {
    NotificationChannel,
    NotificationSubscription,
    NotificationsContext,
    NotificationType,
} from '@gitbook/app-notifications';
import type { BLOCK_TYPES, INLINE_TYPES } from '@gitbook/doc-core';
import type * as hiveCore from '@gitbook/hive-core';

export type AnalyticsOrganizationSettingsScreen =
    | { screen: 'organization_settings_profile' }
    | {
          screen: 'organization_settings_plans';
          reason?: OrganizationUpgradeReason;
          interval?: 'monthly' | 'yearly';
      }
    | { screen: 'organization_settings_members' }
    | { screen: 'organization_settings_teams' }
    | { screen: 'organization_settings_billing' }
    | { screen: 'organization_settings_migrate' }
    | { screen: 'organization_settings_saml' }
    | { screen: 'organization_settings_sso' }
    | { screen: 'organization_settings_integrations' }
    | { screen: 'organization_settings_invites' }
    | { screen: 'organization_settings_rules' }
    | { screen: 'organization_settings_agents' };

/**
 * All our "view" events.
 * List screen you want to track views for, we'll automatically add the prefix "view_" to those events.
 */
export type AnalyticsScreenProperties =
    | { screen: 'login' }
    | { screen: 'login_sso' }
    | { screen: 'login_reset_password' }
    | { screen: 'login_apply_reset_password' }
    | { screen: 'login_apply_signin_link' }
    | { screen: 'login_apply_verify_email' }
    | { screen: 'login_apply_recover_email' }
    | { screen: 'invite' }

    // Onboarding
    // Create an org
    | {
          screen: 'welcome_create';
          reason?: OrganizationUpgradeReason;
      }
    | {
          screen: 'welcome_list_organizations';
      }

    // Account settings
    | { screen: 'account_settings_profile' }
    | { screen: 'account_settings_developer' }
    | { screen: 'account_settings_integrations' }
    | { screen: 'account_settings_notification' }
    | { screen: 'account_settings_organizations' }
    | { screen: 'account_settings_email_update' }
    | { screen: 'account_settings_password_update' }
    | { screen: 'account_settings_mfa_enroll' }
    | { screen: 'account_settings_reauthenticate' }
    | { screen: 'account_settings_verify_email_change' }
    | { screen: 'account_start_with_templates' }
    // Integrations
    | ({ screen: 'integrations_list' } & Partial<AnalyticsContextOrg>)
    | { screen: 'integration' }
    | ((
          | { screen: 'integration_about' }
          | {
                screen: 'integration_installation';
                step: string;
                context?: 'space' | 'site';
                withAccountInstallation: boolean;
                withContentInstallation?: boolean;
            }
          | { screen: 'integration_developer' }
          | { screen: 'integration_space' }
          | { screen: 'integration_spaces' }
      ) &
          (AnalyticsContextIntegration &
              Partial<AnalyticsContextOrg> &
              Partial<AnalyticsContextSpace>))
    // Organization
    | { screen: 'organization_member_invite' }
    | AnalyticsOrganizationSettingsScreen
    | { screen: 'organization_start_with_templates' }
    | { screen: 'organization_home' }
    | { screen: 'organization_change_requests' }
    | { screen: 'organization_end_of_trial' }
    | { screen: 'organization_frozen' }
    | { screen: 'organization_custom_domain' }
    | {
          screen: 'organization_billing_upgrade';
          step: string;
          numberOfSteps: number;
      }
    | {
          screen: 'organization_billing_migrate';
          step: string;
          numberOfSteps: number;
      }
    // Teams
    | { screen: 'team_create' }
    | { screen: 'team_edit' }
    | { screen: 'team_members' }
    // Collections
    | { screen: 'collection_share' }
    // Site
    | { screen: 'sites' }
    | {
          screen: 'site';
          screenTab?: 'insights' | 'settings' | 'preview' | 'customization' | undefined;
          screenSection?:
              | 'features'
              | 'general'
              | 'audience'
              | 'schema'
              | 'domains'
              | 'redirects'
              | 'structure'
              | 'sponsorship'
              | 'legal'
              | 'plan'
              | 'ai'
              | 'embed'
              | undefined;
          /**
           * Is the site empty?
           */
          isEmpty?: boolean;
      }
    | {
          screen: 'site_create';
          screenTab: 'name' | 'content' | 'features' | 'publish';
      }
    | { screen: 'site_custom_domain' }
    | { screen: 'site_customize' }
    | { screen: 'site_audience_modal' }
    | {
          screen: 'site_publish_modal';
          publishType: api.SitePublishOperationPreviewResponse['type'];
      }
    | { screen: 'site_publish_modal_billing' }
    | {
          screen: 'site_plan_modal_plans';
          reason: api.SiteUpgradeReason;
          isNewPricing: boolean;
      }
    | ({
          screen: 'site_plan_modal_billing_legacy';
          reason: api.SiteUpgradeReason;
          plan: api.BillingProduct;
          interval: api.BillingInterval;
      } & Omit<api.BillingInvoicePreview, 'lines'>)
    | ({
          screen: 'site_plan_modal_billing';
          reason: api.SiteUpgradeReason;
          plan: api.SiteType;
          interval: api.BillingInterval;
      } & Omit<api.BillingInvoicePreview, 'lines'>)
    | {
          screen: 'site_insights';
          report: string;
          range?: api.SiteInsightsQueryRange;
          whereColumns?: string[];
          groupByColumns?: string[];
      }
    // Space
    | { screen: 'space'; diffMode: boolean }
    | { screen: 'space_empty_state'; editable: boolean }
    | { screen: 'space_preview' }
    | { screen: 'space_discussion' }
    | { screen: 'space_files' }
    | { screen: 'space_import' }
    | { screen: 'space_export' }
    | { screen: 'space_publish' }
    | { screen: 'space_publish_success' }
    | { screen: 'space_customize' }
    | { screen: 'space_setup_gitsync' }
    | { screen: 'space_gitsync_installation_flow' }
    | { screen: 'space_permissions' }
    | { screen: 'space_share' }
    | { screen: 'space_merge_rules' }
    | { screen: 'space_integrations'; screenTab: string; category?: string }
    | { screen: 'site_integrations'; screenTab: string; category?: string }
    | { screen: 'space_integration'; integration: string }
    | { screen: 'site_integration'; integration: string }
    | ({
          screen: 'space_change_request';
          changeRequestStatus: api.ChangeRequestStatus;
          diffMode: boolean;
          detailsPanel: boolean;
          createdByGitBookAgent: boolean;
      } & AnalyticsContextChangeRequest)
    | { screen: 'space_activity' }
    | { screen: 'space_insights' }
    | { screen: 'space_change_requests' }
    | { screen: 'space_change_request_agent' }
    | { screen: 'space_deleted' }
    | { screen: 'space_restore' }
    | ({ screen: 'space_page_options' } & AnalyticsContextPage)
    | ({ screen: 'space_page_variables' } & AnalyticsContextPage)
    | { screen: 'space_page_notfound' }
    | {
          screen: 'audits_broken_links';
          countBrokenLinksSpace: number;
          countBrokenLinksCR?: number;
          isPreview?: boolean;
      }
    | { screen: 'space_create' }
    // Change request modal
    | { screen: 'space_change_request_edit' }
    | { screen: 'space_change_request_request_review' }
    | { screen: 'space_custom_domain' }
    // Palette
    | { screen: 'palette' }
    // Search
    | { screen: 'search'; searchType: 'ai' | 'search' }
    // Help / Meta
    | { screen: 'help_shortcuts' }
    // Onboarding
    | ({
          screen: 'template';
          template?: string | null;
      } & Partial<AnalyticsContextOrg>)
    // Import
    | { screen: 'import_start'; trigger: ImportEventTrigger }
    | { screen: 'import_start_source'; trigger: ImportEventTrigger; source: 'file' | 'website' }
    // OpenAPI
    | { screen: 'openapi_specs' }
    | { screen: 'openapi_spec' }
    // Translations
    | { screen: 'translations' }
    | { screen: 'translations_glossary' }
    | { screen: 'translation_create_dialog'; intent: TranslationAnalyticsIntent }
    | { screen: 'translation_edit' }
    // Agents
    | { screen: 'agents_tasks' }
    // Adaptive content
    | {
          screen: 'adaptive_content_expression';
          adaptiveContentObject: 'section' | 'variant' | 'page' | 'header-link';
      }
    | { screen: 'adaptive_content_segment' }
    | { screen: 'adaptive_content_schema' };

export type AnalyticsScreen = AnalyticsScreenProperties['screen'];

/**
 * Analytics property when tracking feature flag values.
 */
export type AnalyticsFeatureFlagsProperties = {
    [key in `featureFlag_${FeatureFlagKey}`]?: any;
};

/**
 * Identity for a user.
 */
export interface AnalyticsUserIdentity extends AnalyticsFeatureFlagsProperties {
    userName?: string;
    userEmail?: string;
    userEmailDomain?: string;
    userRiskScore?: number;
    userSignUpProvider: string[];

    // Signup
    userSignUpDate: string;
    userSignUpWeek: string;
    userSignUpMonth: string;
}

/**
 * Identity for an organization.
 */
export interface AnalyticsOrganizationIdentity {
    organizationKey: string;
    organizationName: string;
    organizationType: OrganizationType;
    organizationUseCase?: OrganizationUseCase;
    organizationCommunityType?: OrganizationCommunityType;
    organizationBaseDomain?: string;
    organizationCreateDate: string;
    organizationCreateWeek: string;
    organizationCreateMonth: string;

    // Content usage
    organizationSpaces: number;

    // Members usage
    organizationMembers: number;
    organizationTeams: number;
    organizationGuests: number;
    organizationFreeSeat?: number;

    // Customization
    organizationCustomDomain?: string;
    organizationSubDomain?: string;
    organizationEmailDomains: string[];
    organizationSAMLProviders: string[];
    organizationHasAI: boolean;

    // Billing
    organizationBillingStatus?: OrganizationBillingStatus;
    organizationBillingTrialStatus?: api.BillingTrialStatus;
    organizationBillingTrialCount?: number;
    organizationBillingTrialEnd?: string;
    organizationBillingPriceId?: string;
    organizationBillingProduct?: api.BillingProduct;
    organizationBillingInterval?: api.BillingInterval;
    organizationBillingCustomerId?: string;
    organizationBillingCycle?: string;
    organizationBillingPremiumSite?: number;
    organizationBillingUltimateSite?: number;
    organizationBillingMembers?: number;
    organizationBillingUsageAddons?: api.BillingMeter[];

    // Integrations
    organizationIntegrationsInstalled: string[];

    // Sites
    organizationSiteBasic: number;
    organizationSitePremium: number;
    organizationSiteUltimate: number;
    organizationSiteSponsored: number;
    organizationSiteLegacyBasic: number;
    organizationSiteLegacyPremium: number;

    organizationSiteBasicPublished: number;
    organizationSitePremiumPublished: number;
    organizationSiteUltimatePublished: number;
    organizationSiteSponsoredPublished: number;
    organizationSiteLegacyBasicPublished: number;
    organizationSiteLegacyPremiumPublished: number;
}

/**
 * Identity for a site.
 */
export interface AnalyticsSiteIdentity {
    siteName: string;
    siteType: api.SiteType;
    siteCreateDate: string;
    siteCreateWeek: string;
    siteCreateMonth: string;
    siteVariantsCount: number;
    siteSectionsCount: number;
    siteLinkedSpaceCount: number;

    // Site customizations
    siteHasFullLogoLight: boolean;
    siteHasFullLogoDark: boolean;
    siteTheme: api.CustomizationTheme;
    sitePrimaryColorLight: string;
    sitePrimaryColorDark: string;

    siteDefaultThemeMode: api.CustomizationThemeMode;
    siteThemeModeTogglable: boolean;

    siteFont: api.CustomizationDefaultFont | 'custom';
    siteCornersStyle: api.CustomizationCorners;
    siteDepthStyle: api.CustomizationDepth;
    siteLinksStyle: api.CustomizationLinksStyle;
    siteTint: boolean;

    siteSidebarBackgroundStyle: api.CustomizationSidebarBackgroundStyle;
    siteSidebarListStyle: api.CustomizationSidebarListStyle;

    siteSearchStyle: api.CustomizationSearchStyle;

    siteAnnouncementEnabled: boolean;
    siteAnnouncementStyle: 'info' | 'danger' | 'warning' | 'success';
    siteAnnouncementHasLink: boolean;

    siteHeaderLinksCount: number;
    siteHeaderPrimaryButtonCount: number;
    siteHeaderSecondaryButtonCount: number;
    siteHeaderSubLinksCount: number;

    siteFooterHasLogoLight: boolean;
    siteFooterHasLogoDark: boolean;
    siteFooterHasCopyright: boolean;
    siteFooterGroupsCount: number;
    siteFooterLinksCount: number;

    siteHasSocialPreview: boolean;

    siteIntlLocale: api.CustomizationLocale;
    siteHasAISearch: boolean;
    siteHasExportPDF: boolean;
    siteHasPageRating: boolean;
    siteHasPrivacyPolicy: boolean;
    siteHasAdvancedCustomization: boolean;

    siteHasSiteSpaceCustomizations: boolean;

    // Site visibility settings & publishing state
    siteVisibility: api.SiteVisibility;
    siteIsPublished: boolean;
    siteIsIndexed: boolean;
    siteShareLinksCount: number;
    siteVisitorAuthBackend: 'custom' | 'integration' | undefined;

    siteCustomDomain?: string;
    siteSlug?: string;
    siteCustomSubdirectory?: string;

    siteIntegrationsInstalled: string[];

    // Sponsored site properties
    siteHasAds: boolean;

    // Adaptive content properties
    siteHasAdaptiveContent: boolean;
}

/*
 * Identity for a collection.
 */
export interface AnalyticsCollectionIdentity {
    collectionName: string;
    collectionCreateDate: string;
    collectionCreateWeek: string;
    collectionCreateMonth: string;

    collectionDefaultPermission: DefaultPermissionLevel;
}

/**
 * Identity for a space.
 */
export type AnalyticsSpaceIdentity = {
    spaceName: string;
    spaceCreateDate: string;
    spaceCreateWeek: string;
    spaceCreateMonth: string;
    spaceSlug: string | undefined;

    spaceDefaultPermission: DefaultPermissionLevel;

    spaceHasGitSync: boolean;
    spaceEditMode: string;

    // Footer
    spaceHasFooter?: boolean;
    spaceFooterHasLogo?: boolean;
    spaceFooterHasCopyright?: boolean;
    spaceFooterGroups?: number;
    spaceFooterLinks?: number;

    // Integrations
    spaceIntegrationsInstalled: string[];
};

// Revision traits
export type AnalyticsSpaceRevisionIdentity = {
    spacePagesTotal: number;
    spacePagesSheet: number; // sheets are content
    spacePagesGroup: number;
    spacePagesLink: number;
    spaceFilesTotal: number;
};

export interface AnalyticsPageIdentity {
    spaceId: string;
    pageName: string;
    pageLayout: string;
    pageWidth: string;
    pageHasTitleVisible: boolean;
    pageHasDescriptionVisible: boolean;
    pageHasCover: boolean;
    pageHasCoverVisible: boolean;
    pageCoverSize: string;
    pageHasTableOfContentsVisible: boolean;
    pageHasOutlineVisible: boolean;
    pageHasPaginationVisible: boolean;
    pageHasMetadataVisible: boolean;
}

export type AnalyticsEvent = ClientAnalyticsEvent | ServerAnalyticsEvent;

export interface AnalyticsContextScreen {
    screen: AnalyticsScreen;

    /**
     * The name of the Section of the screen. A Section is a higher-level
     * "area" of the product. For example, "space", "organization".
     *
     * It's typically calculated by looking at the screen name and extracting
     * the higher level section e.g.
     *
     * organization_settings_profile => organization
     * account_settings_templates => account
     * space_import => space
     */
    screenSection?: string;

    /**
     * For Screens with tabs (such as SideSheets), this contains
     * an identifier for the open tab.
     */
    screenTab?: string;
}

/**
 * Context for an event. It links the event to organizations.
 */
export interface AnalyticsContextOrg {
    organization: string;
}
export interface AnalyticsContextSpace {
    space: string;
}
export interface AnalyticsContextSite {
    site: string;
}
export interface AnalyticsContextSiteSpace {
    siteSpace: string;
}
export interface AnalyticsContextPage {
    page: string;
}
export interface AnalyticsContextCollection {
    collection: string;
}
export interface AnalyticsContextChangeRequest {
    changeRequest: string;
}
export interface AnalyticsContextTeam {
    team: string;
}
export interface AnalyticsContextComment {
    comment: string;
}
export interface AnalyticsContextIntegration {
    integration: string;
}

export type AnalyticsEventContext = Partial<AnalyticsContextOrg> &
    Partial<AnalyticsContextSite> &
    Partial<AnalyticsContextSpace> &
    Partial<AnalyticsContextPage> &
    Partial<AnalyticsContextCollection> &
    Partial<AnalyticsContextChangeRequest> &
    Partial<AnalyticsContextTeam> &
    Partial<AnalyticsContextComment> &
    Partial<AnalyticsContextIntegration> &
    Partial<AnalyticsContextScreen>;

/**
 * The different methods a user can authenticate with GitBook
 */
export type AnalyticsAuthenticationMethod =
    | 'link'
    | 'google'
    | 'github'
    | 'github-redirect'
    | 'password'
    | 'email-invite';

/**
 * Events that can be tracked both on the server and the client
 */
export type CommonAnalyticsEvent =
    | ({
          type: 'organization_join';
          method: 'email_domain' | 'email_invite' | 'link_invite' | 'saml';
          role: OrganizationMemberRoleOrGuest;
      } & AnalyticsContextOrg)
    | ({
          type: 'space_join';
          method: 'email_invite' | 'self';
      } & Partial<AnalyticsContextOrg> &
          AnalyticsContextSpace)
    | {
          type: 'auth_sign_up';
          email: string | undefined;
          method: AnalyticsAuthenticationMethod;
          withSSO: boolean;
      };

/**
 * Generic event for moving content. The "scope" is the high-level
 * owner (user or organization). The "container" is the nearest parent
 * of the content (either a collection or the root).
 */
export interface AnalyticsContentMoveEvent {
    action: 'drag-drop' | 'palette';
    fromScopeKey: string;
    fromScopeType: 'org' | 'personal';
    toScopeKey: string;
    toScopeType: 'org' | 'personal';
    fromContainerKey: string;
    fromContainerType: 'root' | 'collection';
    toContainerKey: string;
    toContainerType: 'root' | 'collection';
}

/**
 * Events that are relative to billing
 */
type BillingAnalyticsEvent = (
    | {
          type: 'billing_payment_succeeded';
          $revenue: number;
          attempts: number;
          reason: Stripe.Invoice.BillingReason;
      }

    /** User is starting a free trial */
    | {
          type: 'billing_start_trial';
          product: string;
          interval: string;
          quantity: number;
          reason: OrganizationUpgradeReason;
          days: number;
      }

    /** Trial has finished */
    | {
          type: 'billing_trial_ended';
          product: string;
          interval: string;
          quantity: number;
          premiumSites: number;
          ultimateSites: number;
          status: OrganizationBillingStatus;
      }

    /** User is starting upgrade session */
    | {
          type: 'billing_start_upgrade';
          product: string;
          interval: string;
          quantity: number;
          reason?: OrganizationUpgradeReason;
      }

    /** User has upgraded */
    | {
          type: 'billing_upgrade';
          billingPlan?: string;
          billingProduct?: api.BillingProduct;
          billingInterval: api.BillingInterval;
          billingQuantity: number;

          premiumSites: number;
          ultimateSites: number;

          previousProduct: api.BillingProduct;
          previousPlan?: string;
          previousInterval?: api.BillingInterval;

          reason?: OrganizationUpgradeReason;
      }

    /** User is enabling usage addons */
    | {
          type: 'billing_enable_usage_addon';
          billingMeter: api.BillingMeter;
          billingInterval: api.BillingInterval;
      }

    /** User is disabling usage addons */
    | {
          type: 'billing_disable_usage_addon';
          billingMeter: api.BillingMeter;
          billingInterval: api.BillingInterval;
      }

    /** User is previewing an upgrade to new pricing */
    | {
          type: 'billing_migrate_preview';
          product: string;
          interval: string;
          members: number;
          premiumSites: number;
          ultimateSites: number;
          amountDueToday: number;
          discount?: api.BillingDiscount;
      }

    /** User has migrated to new pricing */
    | {
          type: 'billing_migrate';
          product: string;
          interval: string;
          members: number;
          premiumSites: number;
          ultimateSites: number;
      }

    /** Tracked when user is downgrading to free manually (the actual cancellation is done at end of the period) */
    | {
          type: 'billing_submit_cancel';
          product: string;
          interval: string;
      }

    /** Tracked when user is reactivating a subscription that was set to expire at its end period */
    | {
          type: 'billing_submit_undo_cancel';
          product: string;
          interval: string;
      }

    /** Tracked when user is reactivating their subscription */
    | {
          type: 'billing_reactivate';
          product: string;
          interval: string;
      }

    /** Tracked when the subscription is deleted */
    | {
          type: 'billing_cancel';
          reason:
              | 'end_trial' /** User has not upgraded before the end of the trial */
              | 'cancel_subscription' /** User has cancelled the subscription (manually or stopped paying) */;
          plan?: string;
          quantity: number;
      }
) &
    AnalyticsContextOrg;

/**
 * Event when an API request is received
 */
type APIRequestEvent = {
    type: 'api_request';
    method: string;
    pathname: string;
    client: string;
    operationId?: string;
};

/**
 * Event when a question is asked/answered of the AI
 */
type QuestionAnalyticsEvent = {
    type: 'question_ask';
    question: string;
    // Questions may be asked in a site.
} & Partial<AnalyticsContextSite> &
    AnalyticsContextOrg;

/**
 * Event when an operation has been evaluated for risk, only track when the operation is not allowed.
 */
type SecurityRiskEvaluationEvent = {
    type: 'security_risk_evaluation';
    operationId: string;
    operationType: string;
    scoresAccountAbuse: number;
    scoresAccountTakeOver: number;
    scoresBot: number;
    policyName: string;
    policyId: string;

    /**
     * The evaluation of the request. We log only challenge or deny for now
     */
    policyAction: 'challenge' | 'deny';

    /**
     * List of signals that triggered the policy
     * https://docs.castle.io/docs/signals-reference
     *
     * e.g: ['bot_behavior','proxy_ip','disposable_email','spoofed_device','multiple_accounts_per_device']
     */
    signals: string[];
};

/**
 * All events relative to comments.
 */
export type CommentAnalyticsEvent = (
    | {
          type: 'comment_post';
          context: 'block' | 'space';
          agentInvolved: boolean;
      }
    | {
          type: 'comment_reply';
          agentInvolved: boolean;
      }
    | {
          type: 'comment_resolve';
      }
    | {
          type: 'comment_unresolve';
      }
    | {
          type: 'comment_edit';
      }
    | {
          type: 'comment_delete';
      }
    | {
          type: 'comment_reply_delete';
      }
    | {
          type: 'comment_reply_edit';
      }
    | {
          type: 'comment_add_reaction';
      }
    | {
          type: 'comment_remove_reaction';
      }
    | {
          type: 'comment_reply_add_reaction';
      }
    | {
          type: 'comment_reply_remove_reaction';
      }
) &
    AnalyticsContextComment &
    AnalyticsContextSpaceContent;

type AccountAnalyticsEvent =
    | {
          type: 'account_create_apitoken';
      }
    | {
          type: 'account_delete_apitoken';
      };

type IntegrationClientAnalyticsEvent =
    | {
          type: 'integration_configuration_complete';
          integration: string;
      }
    | {
          type: 'integration_quick_installation';
          integration: string;
          withAccountInstallation: boolean;
      }
    | {
          type: 'integration_featured_select';
      };

/**
 * Events that are tracked on the server side.
 */
export type ServerAnalyticsEvent = (
    | OrganizationAnalyticsEvent
    | SiteAnalyticsEvent
    | AccountAnalyticsEvent
    | CollectionAnalyticsEvent
    | SpaceAnalyticsEvent
    | CommonAnalyticsEvent
    | BillingAnalyticsEvent
    | ChangeRequestAnalyticsEvent
    | IntegrationAnalyticsEvent
    | APIRequestEvent
    | SecurityRiskEvaluationEvent
    | CommentAnalyticsEvent
    | QuestionAnalyticsEvent
    | PublicContentSitePageViewEvent
    | ({
          type: 'space_gitsync_sync';
          operation: 'import' | 'standalone-import' | 'export' | 'standalone-export';
      } & AnalyticsContextSpace)
    | ({
          type: 'space_gitsync_synchronize';
          operation: 'import' | 'standalone-import' | 'export' | 'standalone-export';
      } & AnalyticsContextSpace)
    | ({
          type: 'space_gitsync_sync_start';
      } & GitSyncEventCommonProperties)
    | (GitSyncEndSuccessEvent | GitSyncEndFailureEvent | GitSyncEndDroppedEvent)
    | ({
          type: 'space_gitsync_synchronize_start';
      } & GitSynchronizeEventCommonProperties)
    | (
          | GitSynchronizeEndSuccessEvent
          | GitSynchronizeEndFailureEvent
          | GitSynchronizeEndDroppedEvent
      )
    | AIAnalyticsContentAuditEvents
    | ImportServerAnalyticsEvent
) &
    Partial<AnalyticsEventContext> &
    Partial<AnalyticsAPIProperties>;

/**
 * Properties sent on events that are tracked from within the API.
 */
export interface AnalyticsAPIProperties {
    /** User-Agent doing the request */
    apiUserAgent: string;
    /** Operation ID in the api spec */
    apiOperationId: string;
    /** Type of authentication used */
    apiAuthType?: 'app-token' | 'api-token' | 'installation' | 'integration' | 'content';
    /** The ID of the integration if apiAuthType === 'integration' */
    apiIntegration?: string;
    /** Limit on the rate limiting */
    apiRateLimitLimit?: number;
    /** Remaining requests before rate limiting */
    apiRateLimitRemaining?: number;
}

interface GitSyncCommonProperties {
    // The unique ID of the Git sync process
    gitSyncProcessId: string;
    gitSyncDirection: 'import' | 'export';
    // The pretty branch name, such as "main"
    gitSyncBranch: string;
    // The Git sync `projectDirectory` if specified
    gitSyncProjectDirectory?: string;
    // "main" if the sync targets the main history, "preview" for standalone previews
    gitSyncTarget: 'main' | 'preview';
    // TODO: remove this once we fully switched the git sync over to Inngest
    gitSyncFlowUsingInngest?: boolean;
}

interface GitSyncIntegrationEventProperties extends GitSyncCommonProperties {
    gitSyncProvider: GitSyncProvider['type'];
    // The full repo name, such as "owner/repo"
    gitSyncRepo: string;
}

interface GitSyncAPIEventProperties extends GitSyncCommonProperties {
    // For API events, we don't know the provider and the repo name
    // Instead, we will also pass the full repo URL
    gitSyncProvider: 'unknown';
    // The full repo URL to sync
    gitSyncRepoURL: string;
}

export type GitSyncEventCommonProperties =
    | GitSyncIntegrationEventProperties
    | GitSyncAPIEventProperties;

type GitSyncEndCommonProperties = GitSyncEventCommonProperties & {
    type: 'space_gitsync_sync_end';
    gitSyncStatus: 'success' | 'failure' | 'dropped';
    // In seconds
    gitSyncDuration: number;
};

export interface GitSyncSuccessProperties {
    gitSyncStatus: 'success';
    gitSyncPagesCreated: number;
    gitSyncPagesModified: number;
    gitSyncPagesRemoved: number;
    gitSyncFilesCreated: number;
    gitSyncFilesModified: number;
    gitSyncFilesRemoved: number;
    gitSyncReusableContentCreated: number;
    gitSyncReusableContentModified: number;
    gitSyncReusableContentRemoved: number;
}

type GitSyncEndSuccessEvent = GitSyncEndCommonProperties & GitSyncSuccessProperties;

export interface GitSyncFailureProperties {
    gitSyncStatus: 'failure';
    // Whether the error is exposed to the user or not
    gitSyncErrorSource: 'internal' | 'external';
    // The error message
    gitSyncErrorDescription: string;
}

type GitSyncEndFailureEvent = GitSyncEndCommonProperties & GitSyncFailureProperties;

type GitSyncDroppedReason =
    | {
          // The `gitSyncId` of the sync that prevented this one to be committed
          gitSyncDroppedBy: string;
      }
    | {
          // The reason why the sync was dropped
          gitSyncDroppedReason: string;
      };

export type GitSyncDroppedProperties = {
    gitSyncStatus: 'dropped';
} & GitSyncDroppedReason;

type GitSyncEndDroppedEvent = GitSyncEndCommonProperties & GitSyncDroppedProperties;

export type GitSyncAnyEndProperties =
    | GitSyncSuccessProperties
    | GitSyncFailureProperties
    | GitSyncDroppedProperties;

/**
 * Git Synchronize events
 */
interface GitSynchronizeCommonProperties {
    // The unique ID for the Git sync run
    gitSyncId: string;
    gitSyncDirection: 'import' | 'export' | 'sync';
    // The pretty branch name, such as "main"
    gitSyncBranch: string;
    // The Git sync `projectDirectory` if specified
    gitSyncProjectDirectory?: string;
    // "main" if the sync targets the main history, "preview" for standalone previews
    gitSyncTarget: 'main' | 'preview';
    gitSyncForce?: boolean;
}

interface GitSynchronizeAPIEventProperties extends GitSynchronizeCommonProperties {
    // For API events, we don't know the provider and the repo name
    // Instead, we will also pass the full repo URL
    gitSyncProvider: 'unknown';
    // The full repo URL to sync
    gitSyncRepoURL: string;
}

export type GitSynchronizeEventCommonProperties = GitSynchronizeAPIEventProperties;

type GitSynchronizeEndCommonProperties = GitSynchronizeEventCommonProperties & {
    type: 'space_gitsync_synchronize_end';
    gitSyncStatus: 'success' | 'failure' | 'dropped';
    // In seconds
    gitSyncDuration: number;
};

export interface GitSynchronizeSuccessProperties {
    gitSyncStatus: 'success';
    gitSyncPagesCreated: number;
    gitSyncPagesModified: number;
    gitSyncPagesRemoved: number;
    gitSyncFilesCreated: number;
    gitSyncFilesModified: number;
    gitSyncFilesRemoved: number;
}

type GitSynchronizeEndSuccessEvent = GitSynchronizeEndCommonProperties &
    GitSynchronizeSuccessProperties;

export interface GitSynchronizeFailureProperties {
    gitSyncStatus: 'failure';
    // Whether the error is exposed to the user or not
    gitSyncErrorSource: 'internal' | 'external';
    // The error message
    gitSyncErrorDescription: string;
}

type GitSynchronizeEndFailureEvent = GitSynchronizeEndCommonProperties &
    GitSynchronizeFailureProperties;

export type GitSynchronizeDroppedProperties = {
    gitSyncStatus: 'dropped';
    // The reason why the sync was dropped
    gitSyncDroppedReason: string;
};

type GitSynchronizeEndDroppedEvent = GitSynchronizeEndCommonProperties &
    GitSynchronizeDroppedProperties;

export type GitSynchronizeAnyEndProperties =
    | GitSynchronizeSuccessProperties
    | GitSynchronizeFailureProperties
    | GitSynchronizeDroppedProperties;

/**
 * Feature key causing the user to see the org creation or upgrade.
 */
export type OrganizationUpgradeReason = string;

/**
 * All events relative to an organization.
 */
type OrganizationAnalyticsEvent = (
    | {
          type: 'organization_open_upgrade';
          currentFeaturesSet: api.BillingProduct | null;
          reason: OrganizationUpgradeReason;
          screenTab?: string;
      }
    | {
          type: 'organization_open_site_upgrade';
          reason: OrganizationUpgradeReason;
      }
    | {
          type: 'organization_request_upgrade';
          currentFeaturesSet: api.BillingProduct | null;
          reason: OrganizationUpgradeReason;
      }
    | {
          type: 'organization_request_site_upgrade';
          reason: OrganizationUpgradeReason;
      }
    | {
          type: 'organization_open_billing';
          reason: 'sidebar_cta_payment_failure';
      }
    | {
          type: 'organization_open_enterprise_cta';
      }
    | {
          type: 'organization_create';
          reason?: OrganizationUpgradeReason;
          useCase?: OrganizationUseCase;
          organizationType: OrganizationType;
          communityType?: OrganizationCommunityType;
          emailDomainEnabled: boolean;
      }
    | {
          type: 'organization_delete';
      }
    | {
          type: 'organization_email_invite';
          emails: number;
      }
    | ({
          type: 'organization_create_invite_link';
          role: OrganizationMemberRoleOrGuest;
      } & Partial<AnalyticsContextSpace> &
          Partial<AnalyticsContextCollection>)
    | {
          type: 'organization_revoke_link_invite';
      }
    | {
          type: 'organization_copy_invite_link';
          role: OrganizationMemberRoleOrGuest;
      }
    | {
          type: 'organization_edit_invite_link_role';
          previousRole: OrganizationMemberRoleOrGuest;
          role: OrganizationMemberRoleOrGuest;
      }
    | {
          type: 'organization_edit_invite_link_content_role';
          previousRole: ContentPermissionLevelOrNone;
          role: ContentPermissionLevelOrNone;
      }
    | {
          type: 'organization_edit_name';
      }
    | {
          type: 'organization_edit_hostname';
          hostname: string;
      }
    | {
          type: 'organization_edit_default_content';
          contentType: 'space' | 'collection' | 'site' | null;
      }
    | {
          type: 'organization_edit_custom_domain';
          customDomain: string;
      }
    | {
          type: 'organization_remove_custom_domain';
          customDomain: string;
      }
    | {
          type: 'organization_edit_logo';
      }
    | {
          type: 'organization_edit_merge_rule';
          mergeRulesConfiguration: api.MergeRulesStandaloneConfiguration['type'];
          mergeRules: api.MergeRule['rule'][];
      }
    | {
          type: 'organization_remove_logo';
      }
    | {
          type: 'organization_enable_enforce_sso';
      }
    | {
          type: 'organization_disable_enforce_sso';
      }
    | {
          type: 'organization_edit_email_domains';
          reason?: 'invite';
      }
    | {
          type: 'organization_edit_member';
          previousRole: OrganizationMemberRoleOrGuest;
          role: OrganizationMemberRoleOrGuest;
      }
    | {
          type: 'organization_edit_member_role';
          previousRole: api.TeamMemberRole;
          role: api.TeamMemberRole;
      }
    | {
          type: 'organization_edit_agent_instructions';
      }
    | {
          type: 'organization_link_sso_member';
      }
    | {
          type: 'organization_remove_member';
          role: OrganizationMemberRoleOrGuest;
          removedUser: string;
      }
    | {
          type: 'organization_leave';
          role: OrganizationMemberRoleOrGuest;
      }
    | {
          type: 'organization_create_saml';
      }
    | {
          type: 'organization_edit_saml';
      }
    | {
          type: 'organization_delete_saml';
      }
    | {
          type: 'organization_set_default_role';
          role: OrganizationMemberRoleOrGuest;
      }
    | {
          type: 'organization_enable_invite_links';
      }
    | {
          type: 'organization_disable_invite_links';
      }
    | {
          type: 'organization_trial_decision';
          trialDecision: OrganizationTrialDecision;
      }
    | {
          type: 'organization_enable_ai';
          from: 'search' | 'settings';
      }
    | {
          type: 'organization_disable_ai';
      }
    | {
          type: 'organization_enable_ai_insights';
          from: 'settings';
      }
    | {
          type: 'organization_disable_ai_insights';
      }
    // Custom fonts
    | {
          type: 'organization_create_custom_font';
          fontFamily: string;
      }
    | {
          type: 'organization_update_custom_font';
      }
    | {
          type: 'organization_delete_custom_font';
      }

    /** User is starting to enable a usage addon */
    | {
          type: 'billing_start_enable_usage_addon';
          billingMeter: api.BillingMeter;
          billingInterval: api.BillingInterval;
      }
) &
    AnalyticsContextOrg;

/**
 * All events relative to a site.
 */
export type SiteAnalyticsEvent = (
    | {
          type: 'site_create';
      }
    | {
          type: 'site_create_from_wizard';
          inputName: string;
          inputContent: 'sample' | 'empty';
          inputFeatures?:
              | api.SiteType.Basic
              | api.SiteType.Sponsored
              | api.SiteType.Premium
              | api.SiteType.Ultimate;
      }
    | {
          type: 'site_get_started_task_ignore';
          task: string;
      }
    | {
          type: 'site_get_started_task_action';
          task: string;
      }
    | {
          type: 'site_get_started_dismiss';
          trigger: 'button' | 'automatic';
      }
    | {
          type: 'site_feature_card_dismiss';
          feature: string;
      }
    | {
          type: 'site_feature_card_action';
          feature: string;
      }
    | {
          type: 'site_delete';
      }
    | {
          type: 'site_publish';
      }
    | {
          type: 'site_unpublish';
      }
    | {
          type: 'site_edit_visibility';
          visibility: api.SiteVisibility;
          previousVisibility: api.SiteVisibility;
      }
    | {
          type: 'site_edit_title';
      }
    | {
          type: 'site_edit_type';
          fromSiteType: api.SiteType;
          toSiteType: api.SiteType;
          reason?: string;
      }
    | {
          type: 'site_edit_basename';
      }
    | {
          type: 'site_variant_edit_path';
          siteSpace: api.SiteSpace['id'];
      }
    | {
          type: 'site_variant_add_condition';
          siteSpace: api.SiteSpace['id'];
          condition: string;
      }
    | {
          type: 'site_variant_edit_condition';
          siteSpace: api.SiteSpace['id'];
          condition: string;
      }
    | {
          type: 'site_variant_remove_condition';
          siteSpace: api.SiteSpace['id'];
      }
    | {
          type: 'site_section_edit_title';
          siteSection: api.SiteSection['id'];
      }
    | {
          type: 'site_section_edit_description';
          siteSection: api.SiteSection['id'];
      }
    | {
          type: 'site_section_edit_icon';
          siteSection: api.SiteSection['id'];
      }
    | {
          type: 'site_section_edit_path';
          siteSection: api.SiteSection['id'];
      }
    | {
          type: 'site_section_edit_default_site_space';
          siteSection: api.SiteSection['id'];
      }
    | {
          type: 'site_section_add_condition';
          siteSection: api.SiteSection['id'];
          condition: string;
      }
    | {
          type: 'site_section_edit_condition';
          siteSection: api.SiteSection['id'];
          condition: string;
      }
    | {
          type: 'site_section_remove_condition';
          siteSection: api.SiteSection['id'];
      }
    | {
          type: 'site_edit_default_site_space';
      }
    | {
          type: 'site_configure_proxy';
      }
    | {
          type: 'site_remove_proxy';
      }
    | ({
          type: 'site_add_variant';
      } & AnalyticsContextSpace)
    | ({
          type: 'site_remove_variant';
      } & AnalyticsContextSpace)
    | {
          type: 'site_remove_section';
          siteSection: api.SiteSection['id'];
      }
    | {
          type: 'site_add_section';
      }
    | {
          type: 'site_add_section_group';
      }
    | {
          type: 'site_remove_section_group';
          siteSectionGroup: api.SiteSectionGroup['id'];
      }
    | {
          type: 'site_section_group_edit_title';
          siteSectionGroup: api.SiteSectionGroup['id'];
      }
    | {
          type: 'site_section_group_edit_icon';
          siteSectionGroup: api.SiteSectionGroup['id'];
      }
    | {
          type: 'site_section_group_remove_section';
          siteSectionGroup: api.SiteSectionGroup['id'];
      }
    | {
          type: 'site_section_group_add_section';
          siteSectionGroup: api.SiteSectionGroup['id'];
      }
    | {
          type: 'site_add_redirect';
      }
    | {
          type: 'site_remove_redirect';
      }
    | {
          type: 'site_edit_redirect';
      }
    // Share links
    | {
          type: 'site_edit_publish_create_share_link';
      }
    | {
          type: 'site_edit_publish_disable_share_link';
      }
    | {
          type: 'site_edit_publish_enable_share_link';
      }
    | {
          type: 'site_edit_publish_rename_share_link';
      }
    | {
          type: 'site_edit_publish_revoke_share_link';
      }
    // Adaptive content
    | {
          type: 'adaptive_content_enable';
          source:
              | 'section'
              | 'variant'
              | 'page'
              | 'header-link'
              | 'if-block'
              | 'inline-expr'
              | 'settings'
              | 'feature-card';
      }
    | {
          type: 'adaptive_content_add_schema';
      }
    | {
          type: 'adaptive_content_edit_schema';
      }
    // Custom domains
    | {
          type: 'site_edit_custom_domain';
          customDomain: string;
      }
    | {
          type: 'site_remove_custom_domain';
          customDomain: string;
      }
    | {
          type: 'site_add_custom_domain_cta';
          needsUpgrade: boolean;
      }
    // Publish options
    | {
          type: 'site_edit_publish_customization';
          contentProperties: string[];
          siteSpace?: api.SiteSpace['id'];
      }
    | {
          type: 'site_preview_publish_customization';
          contentProperties: string[];
          siteSpace?: api.SiteSpace['id'];
      }
    | {
          type: 'site_edit_publish_upload_footer_logo';
      }
    | {
          type: 'site_edit_publish_upload_full_logo';
      }
    | {
          type: 'site_edit_publish_upload_socialpreview';
      }
    | {
          type: 'site_publish_view_breakdown';
      }
    | {
          type: 'site_manage_share_links';
          triggerLocation: 'published-dialog' | 'header';
      }
    | {
          type: 'site_manage_authenticated_access';
          triggerLocation: 'published-dialog' | 'header';
      }
    | {
          type: 'site_share';
          triggerLocation: 'published-dialog';
          platform: 'twitter' | 'linkedin' | 'reddit';
      }
    | {
          type: 'site_copy_link';
          triggerLocation: 'published-dialog';
      }
    // Settings
    | {
          type: 'site_toggle_ai_search';
          toggleStatus: boolean;
      }
    | {
          type: 'site_toggle_ai_assistant';
          toggleStatus: boolean;
      }
    | {
          type: 'site_embed_panel_hover';
      }
    // MCP servers
    | {
          type: 'site_create_mcp_server';
      }
    | {
          type: 'site_edit_mcp_server';
      }
    | {
          type: 'site_delete_mcp_server';
      }
    | {
          type: 'site_toggle_export_pdf';
          toggleStatus: boolean;
      }
    | {
          type: 'site_toggle_advanced_customization';
          toggleStatus: boolean;
      }
    | { type: 'site_toggle_page_feedback'; toggleStatus: boolean }
    | { type: 'site_open_upgrade'; reason: string; plan: api.BillingProduct }
    | {
          type: 'site_open_change_plan_dialog';
          reason: api.SiteUpgradeReason;
          currentPlan: api.SiteType;
          selectedPlan: api.SiteType;
      }
    | { type: 'site_ads_open_payout' }
    | { type: 'site_ads_enable' }
    | { type: 'site_ads_disable' }
    | { type: 'site_ads_submit_review'; contentTopic: api.SiteAdsTopic }
    | { type: 'site_ads_review_approve' }
    | { type: 'site_ads_review_reject' }
    // Empty state
    | {
          type: 'site_content_template_preview';
          templateKey: string;
          trigger: 'button' | 'card';
      }
    | ({
          type: 'site_content_create_intent';
      } & (
          | {
                source: 'blank';
            }
          | {
                source: 'space';
            }
          | {
                source: 'template';
                templateKey: string;
            }
          | {
                source: 'import';
                sourceType: 'gitsync' | 'openapi' | 'other';
            }
      ))
    // AI
    | {
          type: 'site_stream_ai_response';
          [key: `tool_${string}`]: number;
      }
) &
    AnalyticsContextSite;

/**
 * All events relative to a collection.
 */
type CollectionAnalyticsEvent = (
    | {
          type: 'collection_create';
          restricted: boolean;
      }
    | {
          type: 'collection_delete';
      }
    | ({
          type: 'collection_move';
      } & AnalyticsContentMoveEvent)
    | {
          type: 'collection_edit_name';
      }
    | {
          type: 'collection_edit_description';
      }
    | {
          type: 'collection_edit_permissions';
          teamsAdded: number;
          usersAdded: number;
          guestsAdded: number;
          permissionLevel: ContentPermissionLevelOrNone;
      }
    | {
          type: 'collection_edit_default_permissions';
          permissionLevel: DefaultPermissionLevel;
      }
) &
    Partial<AnalyticsContextOrg> &
    AnalyticsContextCollection;

/**
 * All events relative to a change request.
 */
export type ChangeRequestAnalyticsEvent =
    /** Event in the API when the change request is created */
    (
        | {
              type: 'change_request_create';
              mode?: 'agent';
          }
        | {
              type: 'change_request_merge';
              isFastForward: boolean;
              changeRequestStatus: api.ChangeRequestStatus;
              changeRequestEditCount: number;
              changeRequestCommentCount: number;
              mergedFrom: 'draft' | 'review';
              createdByGitBookAgent: boolean;
          }
        | {
              type: 'change_request_start_conflicts';
          }
        | {
              type: 'change_request_archive';
          }
        | {
              type: 'change_request_unarchive';
          }
        | {
              type: 'change_request_edit_subject';
          }
        | {
              type: 'change_request_edit_description';
          }
        | {
              type: 'change_request_submit';
          }
        | {
              type: 'change_request_update';
          }
        | {
              type: 'change_request_open_preview';
          }
        | {
              type: 'change_request_reverted_to_draft';
          }
        | {
              type: 'change_request_open_primary_content';
          }
        | {
              /**
               * Triggered when the user open a change request from the sidepanel
               */
              type: 'change_request_sidepanel_open';
              tab: string;
          }
        /**
         * Reviews
         */
        | {
              type: 'change_request_review';
              status: api.ChangeRequestReviewStatus;
              withComment: boolean;
              createdByGitBookAgent: boolean;
          }
        | {
              /** Triggered when a review is requested on a change request */
              type: 'change_request_request_review';

              /** the number of users who were requested */
              users: number;

              /** Whether a subject was submitted with the request for review. */
              withSubject: boolean;
          }
        | {
              /** Triggered when a reviewer is removed on a change request */
              type: 'change_request_remove_reviewer';
          }
        | {
              /** Triggered when overview side panel is opened */
              type: 'change_request_details_panel_open';
          }
        | {
              /** Triggered when overview side panel is closed */
              type: 'change_request_details_panel_close';
          }
        | {
              type: 'change_request_pick_flows_open';
              spaceSyncStatus: 'idle' | 'syncing';
          }
    ) &
        Partial<AnalyticsContextOrg> &
        AnalyticsContextSpace &
        AnalyticsContextChangeRequest;

/**
 * All events relative to a team.
 */
export type TeamAnalyticsEvent = (
    | {
          type: 'team_create';
      }
    | {
          type: 'team_delete';
      }
    | {
          type: 'team_edit_name';
      }
    | {
          type: 'team_add_members';
          members: number;
      }
    | {
          type: 'team_remove_members';
          members: number;
      }
) &
    AnalyticsContextTeam &
    AnalyticsContextOrg;

export type SpaceAnalyticsExportSource = 'pdf';

/**
 * All events relative to a page of a space.
 */
export type PageAnalyticsEvent = {
    type: 'page_edit_options';
    pageProperties: string[];
} & AnalyticsContextPage &
    AnalyticsContextSpace &
    Partial<AnalyticsContextOrg>;

export type PublicContentSpacePageViewEvent = {
    type: 'public_content_space_page_view';
    url: string;
    urlHostname: string;
    urlPathname: string;
    referrer: string;
} & AnalyticsContextSpace &
    Partial<AnalyticsContextOrg> &
    Partial<AnalyticsContextCollection> &
    Partial<AnalyticsContextPage>;

export type PublicContentSitePageViewEvent = {
    type: 'public_content_site_page_view';
    url: string;
    urlHostname: string;
    urlPathname: string;
    referrer: string;
    /**
     * List of custom claim keys attached to the visitor, if any.
     */
    visitorAuthClaims?: string[];
} & Partial<AnalyticsContextSpace> &
    AnalyticsContextSite &
    Partial<AnalyticsContextSiteSpace> &
    Partial<AnalyticsContextOrg> &
    Partial<AnalyticsContextCollection> &
    Partial<AnalyticsContextPage>;

export type ChangeRequestCreateSource =
    | 'space-edit-button'
    | 'space-empty-state'
    | 'space-keyboard-shortcut'
    | 'org-create-button'
    | 'org-agent-button'
    | 'space-agent-button'
    | 'site-get-started-action';

/**
 * All events relative to a space.
 */
export type SpaceAnalyticsEvent = (
    | {
          type: 'space_create';
      }
    | {
          type: 'space_delete';
      }
    | {
          type: 'space_restore_deleted';
      }
    | {
          type: 'space_edit_name';
      }
    | {
          type: 'space_edit_language';
          language: api.TranslationLanguage;
      }
    | {
          type: 'space_edit_emoji';
          emoji: string;
      }
    | {
          type: 'space_edit_icon';
      }
    | {
          type: 'space_edit_merge_rule';
          mergeRulesConfiguration: api.MergeRulesSpaceConfiguration['type'];
          mergeRules: api.MergeRule['rule'][];
      }
    | {
          type: 'space_remove_icon';
      }
    | {
          type: 'space_export';
          source: SpaceAnalyticsExportSource;
      }
    | {
          type: 'space_duplicate';
      }
    | {
          type: 'space_copy_url';
      }
    | ({
          type: 'space_move';
      } & AnalyticsContentMoveEvent)
    | {
          type: 'space_edit_custom_domain';
          customDomain: string;
      }
    | {
          type: 'space_remove_custom_domain';
          customDomain: string;
      }
    | {
          type: 'space_edit_permissions';
          guestsAdded: number;
          teamsAdded: number;
          usersAdded: number;
          permissionLevel: ContentPermissionLevelOrNone;
      }
    | {
          type: 'space_edit_default_permissions';
          permissionLevel: DefaultPermissionLevel;
      }
    | {
          type: 'space_edit_visibility';
          visibility: api.ContentVisibility;
          previousVisibility: api.ContentVisibility;
      }
    | {
          type: 'space_edit_gitsync';
          provider: GitSyncProvider['type'];
          withProjectDirectory: boolean;
          experimentalVersion?: number;
      }
    | {
          type: 'space_install_gitsync';
          provider: GitSyncProvider['type'];
          withProjectDirectory: boolean;
          experimentalVersion?: number;
      }
    | {
          type: 'space_remove_gitsync';
          provider: GitSyncProvider['type'];
      }
    | {
          type: 'space_edit_edit_mode';
          editMode: api.SpaceEditMode;
      }
    | {
          type: 'space_blank_slate_initiate';
          kind: string;
      }
    | {
          type: 'space_diff_edit_mode';
          diffEnabled: boolean;
          diffMode: 'only-changes' | 'all-pages';
      }
    /** Event when the user clicks to create a change request */
    | {
          type: 'change_request_start_create';
          source: ChangeRequestCreateSource;
      }
    | PublicContentSpacePageViewEvent
) &
    AnalyticsContextSpace &
    Partial<AnalyticsContextOrg> &
    Partial<AnalyticsContextCollection>;

/**
 * All events related to AI features.
 */
export type SearchAnalyticsEvents =
    | {
          type: 'search_pick_suggestion_recent_search';
          suggestionIndex: number;
      }
    | {
          type: 'search_pick_suggestion_recent_ai_search';
          suggestionIndex: number;
      }
    | {
          type: 'search_pick_suggestion_space';
          suggestionIndex: number;
      }
    | {
          type: 'search_pick_suggestion_question';
      }
    | {
          type: 'search_type';
          query: string;
      }
    | {
          type: 'search_open_result';
          resultType: 'page' | 'section' | 'space' | 'collection';
          resultIndex: number;
      };

/**
 * All events related to AI features.
 */
export type AIAnalyticsEvents = (
    | {
          type: 'ai_search_pick_suggestion_recent_search';
          suggestionIndex: number;
      }
    | {
          type: 'ai_search_pick_suggestion_question';
      }
    | {
          type: 'ai_search_submit';
          query: string;
          trigger: 'click_ask_gitbook' | 'press_enter' | 'query_param';
      }
    | {
          type: 'ai_search_pick_followup';
          query: string;
          followup: string;
          previousQueriesCount: number;
      }
    // Answer has been loaded and displayed
    | {
          type: 'ai_search_view_answer';
          query: string;
          hasAnswer: boolean;
          queriesInMemory: number;
          hasFollowupQuestions: boolean;
      }
    // User is opening a source
    | {
          type: 'ai_search_open_source';
      }
    // Error when fetching/rendering answer
    | {
          type: 'ai_search_crash';
          query: string;
      }
    // Feedback from user on answer
    | {
          type: 'ai_search_downvote_answer';
          query: string;
      }
    | {
          type: 'ai_search_upvote_answer';
          query: string;
      }
    | {
          type: 'ai_search_copy_answer';
          query: string;
      }
) &
    Partial<AnalyticsContextSpace> &
    Partial<AnalyticsContextOrg>;

/**
 * All events related to AI features.
 */
export type ChangeRequestAgentsAnalyticsEvents = (
    | {
          type: 'change_request_agent_message_post';
          previousMessagesCount: number;
          message: string;
          messageSource: 'input' | 'suggestion';
      }
    | {
          type: 'change_request_agent_reset';
          messagesCount: number;
      }
) &
    Partial<AnalyticsContextSpace> &
    Partial<AnalyticsContextOrg> &
    Partial<AnalyticsContextChangeRequest>;

export type AIAnalyticsContentAuditEvents =
    | {
          type: 'ai_content_audit_start';
          model: string;
          // the run id of the audit, helps with funnel analysis
          funnelId?: string;
          // to determine if this was a full/first organization audit or a partial one
          fullAudit: boolean;
          numberOfSpaces: number;
          numberOfPagesAdded: number;
          numberOfPagesRemoved: number;
      }
    | ({
          type: 'ai_content_audit_complete';
          model: string;
          // the run id of the audit, helps with funnel analysis
          funnelId?: string;
          cost: number;
          duration: number;
          // to determine how many calls we made to the AI models
          numberOfAiCalls: number;
          // total number of all relations type returned by the models
          numberOfRelations: number;
          // plus one counter per type
          numberOfContradictions: number;
          numberOfDuplicates: number;
      } & Partial<AnalyticsContextOrg>);

/** Insights */
export type InsightsClientAnalyticsEvent = (
    | { type: 'insights_scores_download' }
    | ({ type: 'insights_scores_space_filter' } & AnalyticsContextSpace)
    | { type: 'insights_search_analytics_download' }
    | ({ type: 'insights_search_analytics_space_filter' } & AnalyticsContextSpace)
    | { type: 'insights_search_analytics_open_install_integration' }
) &
    AnalyticsContextOrg;

/** Broken Links */
export type BrokenLinksAnalyticsEvent = {
    type: 'audits_broken_links_source_open';
};

/**
 * Context for all space events.
 */
export type AnalyticsContextSpaceContent = AnalyticsContextSpace &
    Partial<AnalyticsContextChangeRequest> &
    Partial<AnalyticsContextOrg> &
    Partial<AnalyticsContextCollection> &
    Partial<AnalyticsContextPage>;

export type EditAnalyticsBlockType =
    | 'paragraph'
    | 'heading-1'
    | 'heading-2'
    | 'heading-3'
    | 'list-unordered'
    | 'list-ordered'
    | 'list-tasks'
    | 'hint'
    | 'blockquote'
    | 'code'
    | 'tabs'
    | 'expandable'
    | 'drawing'
    | 'math'
    | 'divider'
    | 'table'
    | 'stepper'
    | 'images'
    | 'columns'
    | 'if'
    | 'updates';

export type RawInsertBlockAnalyticsEventProps =
    | {
          blockType: Exclude<EditAnalyticsBlockType, 'table'>;
      }
    | ({
          blockType: 'integration';
          integrationBlock: string;
      } & AnalyticsContextIntegration)
    | {
          blockType: 'swagger';
          swaggerSource?: 'url' | 'file' | 'openapi';
          swaggerOperations?: number;
      }
    | {
          blockType: 'openapi-operation';
          openAPIOperations: number;
          openAPISpecSlug: string;
      }
    | {
          blockType: 'openapi-schemas';
          openAPISchemas: number;
      }
    | {
          blockType: 'embed';
          embedHostname: string;
      }
    | {
          blockType: 'file';
          files: number;
      }
    | {
          blockType: 'images';
          images: number;
      }
    | {
          blockType: 'table';
          tableView: 'grid' | 'cards';
      }
    | {
          blockType: 'content-ref';
          contentRefKind: string;
      }
    | {
          blockType: 'reusable-content';
          reusableContentId: string;
      };

export type RawInsertBlockAnalyticsEvent = {
    type: 'edit_page_document_insert_block';
    from?: 'palette' | 'embed' | 'quick-insert';
} & RawInsertBlockAnalyticsEventProps;

export type RawInsertInlineAnalyticsEventProps =
    | {
          inlineType: 'mention';
          kind: ContentRefKind;
      }
    | { inlineType: 'button' | 'inline-math' | 'expression' | 'icon' | 'emoji' | 'link' };

export type RawInsertInlineAnalyticsEvent = {
    type: 'edit_page_document_insert_inline';
    from: 'block-palette' | 'inline-palette' | 'keyboard-shortcut';
} & RawInsertInlineAnalyticsEventProps;

export type RawQuickstartAnalyticsEventAction =
    | 'intro'
    | 'two-columns'
    | 'multi-syntax-code'
    | 'step-by-step-guide'
    | 'use-template'
    | 'import-content';

export type RawQuickstartAnalyticsEvent = {
    type: 'edit_page_document_quickstart_action';
    action: RawQuickstartAnalyticsEventAction;
};

/**
 * All events relative to editing.
 */
export type RawEditAnalyticsEvent =
    // Insertions
    | {
          type: 'edit_create_page';
          pageKind: hiveCore.RevisionPageType.Document | hiveCore.RevisionPageType.Group;
      }
    | {
          type: 'edit_create_page';
          pageKind: hiveCore.RevisionPageType.Link;
          targetKind: ContentRefKind;
      }
    | {
          type: 'edit_create_page';
          pageKind: hiveCore.RevisionPageType.Computed;
          sourceType: api.ComputedContentSourceRevisionOpenAPI['type'];
          openAPIModelsPage: boolean;
      }
    | {
          type: 'edit_create_page';
          pageKind: hiveCore.RevisionPageType.Computed;
          sourceType: api.ComputedContentSourceRevisionTranslation['type'];
      }
    | {
          type: 'edit_create_page';
          pageKind: hiveCore.RevisionPageType.Computed;
          sourceType: api.ComputedContentSourceIntegration['type'];
      }
    // Removals and edits
    | {
          type: 'edit_remove_page';
          pageKind: hiveCore.RevisionPageType;
      }
    | {
          type: 'edit_page_title';
          pageKind: hiveCore.RevisionPageType;
      }
    | {
          type: 'edit_page_link_title';
          pageKind: hiveCore.RevisionPageType;
      }
    | {
          type: 'edit_page_path';
          pageKind: hiveCore.RevisionPageType;
      }
    | {
          type: 'edit_page_description';
          pageKind: hiveCore.RevisionPageType;
      }
    | {
          type: 'edit_duplicate_page';
          pageKind: hiveCore.RevisionPageType;
      }
    | {
          type: 'edit_page_layout';
          pageKind: hiveCore.RevisionPageType;
          pageLayout: hiveCore.PageLayout;
      }
    | {
          type: 'edit_conflicts_keep_page';
          pageKind: hiveCore.RevisionPageType;
      }

    /** Tracked when user starts editing a page */
    | {
          type: 'edit_page_document_start';

          /** Count of other people editing the page at the same time */
          collaborators: number;
      }

    /** Tracked when user press CMD+S */
    | {
          type: 'edit_manual_save';
          hadChanges: boolean;
      }

    /** Tracked when a user updates a computed page */
    | {
          type: 'edit_page_computed_source';
          sourceType: api.ComputedContentSourceRevisionOpenAPI['type'];
          openAPIModelsPage: boolean;
      }
    | {
          type: 'edit_page_computed_source';
          sourceType: api.ComputedContentSourceIntegration['type'];
      }

    /** Tracked when user go back to editing a page */
    | {
          type: 'edit_page_document_resume';

          /** Count of other people editing the page at the same time */
          collaborators: number;
      }
    | {
          type: 'edit_pick_page_emoji';
          emoji: string;
          pageKind: hiveCore.RevisionPageType;
      }
    | {
          type: 'edit_pick_page_icon';
          icon: string;
          pageKind: hiveCore.RevisionPageType;
      }
    | {
          type: 'edit_remove_page_emoji';
          pageKind: hiveCore.RevisionPageType;
      }
    | {
          type: 'edit_remove_page_icon';
          pageKind: hiveCore.RevisionPageType;
      }
    | {
          type: 'edit_move_page';
          pageKind: hiveCore.RevisionPageType;
      }
    | {
          type: 'edit_add_cover';
      }
    | {
          type: 'edit_cover_height';
          height: number;
      }
    | {
          type: 'edit_toggle_hidden';
          hidden: boolean;
      }
    | {
          type: 'edit_upload_cover';
          coverMode: 'light' | 'dark' | 'all';
      }
    | {
          type: 'edit_remove_cover';
      }
    | {
          type: 'edit_cover_size';
          coverSize: hiveCore.PageLayoutOptionsCoverSize;
      }
    | {
          type: 'edit_reposition_cover';
      }
    | {
          type: 'edit_add_condition';
          condition: string;
      }
    | {
          type: 'edit_edit_condition';
          condition: string;
      }
    | {
          type: 'edit_remove_condition';
      }
    | {
          type: 'edit_upload_files';
          files: number;
          images: number;
      }
    | {
          type: 'edit_remove_file';
      }
    | {
          type: 'edit_download_file';
      }
    | {
          type: 'edit_rename_file';
      }
    | {
          type: 'edit_replace_file';
      }
    /** Track when user pastes or drops something into the editor */
    | {
          type: 'edit_paste_content';

          // droped or pasted content
          paste: 'paste' | 'drop';

          // list of elements included in the clipboard, e.g image, table, etc
          // limited to what's interesting for analysis
          sourceIncludes: string[];

          // received from clipboard
          sourceTypes?: string[];

          // internal structure, transformed from paste
          transformType: string; //
          transformSize: number; // nr of document nodes created from paste
          transformNodes: string[]; // list of node types constructed from paste, e.g paragraph, inline-image, etc.
          uploadedFiles: number; // nr of files uploaded from paste
      }
    | {
          type: 'edit_pick_template';
          template: string;
      }
    | {
          type: 'edit_rollback_revision';
      }
    | ({
          type: 'edit_open_integration';
          reason: 'embed' | 'empty';
      } & AnalyticsContextIntegration)
    | {
          type: 'edit_page_document_insert_quickstart';
          quickstart: string;
      }
    | RawQuickstartAnalyticsEvent
    | RawInsertBlockAnalyticsEvent
    | RawInsertInlineAnalyticsEvent
    | {
          type: 'edit_page_document_move_block';
          method: 'dnd'; // in the future we can imagine tracking other move methods
          blockType: EditAnalyticsBlockType | 'table-row';
          fromParent: EditAnalyticsBlockType | 'document' | 'fragment' | undefined;
          toParent: EditAnalyticsBlockType | 'document' | 'fragment';
      }
    | {
          type: 'edit_page_document_move_block_range';
          method: 'dnd';
          rangeSize: number;
      }
    | {
          type: 'edit_page_document_edit_block_type';
          // in future when we implement more block types, we'll add them here
          fromType: INLINE_TYPES | BLOCK_TYPES;
          toType: INLINE_TYPES | BLOCK_TYPES;
      }
    | {
          type: 'edit_page_document_edit_block_property';
          blockType: string;
          blockProperty: string;
          fromValue: string | undefined;
          toValue: string;
      }
    | {
          type: 'edit_switch_selection_mode';
          selectionMode: 'block' | 'text';
      }
    | {
          type: 'edit_drag_selection';
          size: number;
      }
    | {
          type: 'edit_drawing_generate';
          from: 'empty' | 'editor';
          prompt: string;
          isSuggestion: boolean;
          successGeneration: boolean;
          successParsing: boolean;
      }
    | {
          type: 'edit_drawing_edit';
          existing: boolean;
      }
    | {
          /** User inserts the AI block suggestion */
          type: 'edit_insert_block_completion';
          /** How long it took to generate the completion (ms) */
          generationTime: number;
          /** Type of suggestion */
          suggestionType: 'markdown' | 'diagram';
          /** Length of the suggestion text */
          suggestionLength: number;
          suggestionSummary: string;
          /** The way the completion was inserted */
          insertionMethod: 'click' | 'keyboard';
      }
    | {
          /** User opens the input to insert an AI block in the editor */
          type: 'edit_start_block_completion_prompt';
          /** Trigger that opens the block completion prompt */
          trigger:
              | 'keyboard-space'
              | 'block-button'
              | 'block-options'
              | 'commands-palette'
              | 'hovering-menu'
              | 'keyboard-mod-j';
      }
    | {
          /** Run completion */
          type: 'edit_ai_completion_submit';
          /** Completion ID */
          completionId: string;
          /** Text prompt used */
          prompt: string;
          /** Type of operation */
          operation: 'insert' | 'replace';
          /** Trigger that runs the block completion prompt */
          trigger: 'keyboard-enter' | 'palette-command' | 'submit-button' | 'retry';
      }
    | {
          /** Trigger when the completion is fully done */
          type: 'edit_ai_completion_complete';
          /** Completion ID */
          completionId: string;
          /** Text prompt used */
          prompt: string;
          /** How long it took to generate the completion (ms) */
          executionTime: number;
      }
    | {
          /** Trigger when the completion is stopped by the user. */
          type: 'edit_ai_completion_stop';
          /** Completion ID */
          completionId: string;
          /** Type of operation */
          operation: 'insert' | 'replace';
          /** Text prompt used */
          prompt: string;
          /** How long it ran before the user stopped it. */
          runTime: number;
          /** Trigger that runs the block completion prompt */
          trigger: 'keyboard-escape';
      }
    | {
          /** Trigger when the completion is stopped by the user. */
          type: 'edit_ai_completion_retry';
          /** Completion ID */
          completionId: string;
          /** Type of operation */
          operation: 'insert' | 'replace';
          /** Text prompt used */
          prompt: string;
          /** Trigger that runs the block completion prompt */
          trigger: 'keyboard-alt-r' | 'palette-command';
      }
    | {
          /** Set a prompt (to complete) from the palette */
          type: 'edit_pick_ai_completion_palette';
          /** Prompt setted from the palette */
          prompt: string;
      }
    | {
          /** Detach reusable content */
          type: 'edit_reusable_content_detach';
      }
    | {
          type: 'reusable_content_create';
          method: 'sidebar' | 'editor-palette' | 'editor-prompt-on-copy';
      }
    | {
          type: 'reusable_content_rename';
          method: 'sidebar' | 'block';
      }
    | {
          type: 'reusable_content_delete';
      }
    | {
          type: 'reusable_content_insert';
          method: 'dnd' | 'palette';
      }
    | {
          type: 'page_add_variable';
          variableName: string;
      }
    | {
          type: 'page_edit_variable';
          variableName: string;
      }
    | {
          type: 'page_remove_variable';
          variableName: string;
      }
    | {
          type: 'space_add_variable';
          variableName: string;
      }
    | {
          type: 'space_edit_variable';
          variableName: string;
      }
    | {
          type: 'space_remove_variable';
          variableName: string;
      };

/**
 * An edit event, complete with the context of the containing space.
 */
type SpaceEditAnalyticsEvent = RawEditAnalyticsEvent & AnalyticsContextSpaceContent;

export type TemplatesAnalyticsEvents = Partial<AnalyticsContextSpaceContent> &
    Partial<AnalyticsContextOrg> & {
        type: 'templates_pick_template';
        template: string;
    };

/**
 * All events relative to integrations.
 */
export type IntegrationAnalyticsEvent = (
    | {
          type: 'integration_publish';
          visibility: api.IntegrationVisibility;
      }
    | {
          type: 'integration_unpublish';
          visibility: api.IntegrationVisibility;
      }
    | {
          type: 'integration_install';
          /** If true, the installation was done on the account at the same time as on a space. */
          withSpaceInstallation: boolean;
      }
    | {
          type: 'integration_uninstall';
          installedSpaces: number;
      }
    | {
          type: 'integration_installation_toggle_space_selection';
          spaceSelection: IntegrationInstallationSpaceSelection;
      }
    | ({
          type: 'integration_space_install';
          /** If true, the installation was done on the space at the same time as on the account. */
          withAccountInstallation: boolean;
      } & AnalyticsContextSpace)
    | ({
          type: 'integration_space_uninstall';
      } & AnalyticsContextSpace)
    | ({
          type: 'integration_site_install';
          withAccountInstallation: boolean;
      } & AnalyticsContextSite)
    | ({
          type: 'integration_site_uninstall';
      } & AnalyticsContextSite)
    | {
          type: 'integration_installation_toggle_site_selection';
          siteSelection: api.IntegrationInstallationSiteSelection;
      }
) &
    AnalyticsContextIntegration &
    Partial<AnalyticsContextOrg>;

export type ImportEventTrigger = 'site_empty_state' | 'space_creation' | 'content';

export type ImportEventSource = 'file' | 'website';

export type ImportClientAnalyticsEvent =
    | {
          type: 'import_start';
          source: ImportEventSource;
          sourceURL: string | null;
          trigger: ImportEventTrigger;
          enhance: boolean;
      }
    | {
          type: 'import_open';
          status: api.ContentImportRun['status'];
          trigger: 'site_overview' | 'toast';
      };

export type ImportServerAnalyticsEvent = {
    type: 'import_end';
    status: 'completed' | 'failed';
    source: ImportEventSource;
    sourceURL: string | null;
    importedPages: number;
    space: string;
    organization: string;
    changeRequest?: string;
};

/**
 * All events relative to public content.
 */
export type PublicContentAnalyticsEvent = (
    | {
          type: 'public_content_page_feedback_post';
          rating: api.PageFeedbackRating;
      }
    | {
          type: 'public_content_click_trademark';
      }
    | {
          type: 'public_content_search_open_result';
          resultType: 'page' | 'section';
          resultIndex: number;
      }
    | {
          type: 'public_content_search_type';
          query: string;
      }
    | {
          type: 'public_content_toggle_theme_mode';
          themeMode: ContentThemeMode;
      }
) &
    (AnalyticsContextSpaceContent | AnalyticsContextCollection);

/**
 * All events that act on all notifications at once.
 */
export type AllNotificationAnalyticsEvent = {
    type: 'notification_mark_all_as_read';
};

/**
 * Common properties for notifications analytics.
 */
export interface AnalyticsContextNotification extends NotificationsContext {
    notification: string;

    // whether the notification was read when the action was performed
    isRead: boolean;
}

/**
 * All events relative to notifications.
 */
export type NotificationsAnalyticsEvent = (
    | {
          type: 'notification_open';
      }
    | {
          type: 'notification_mark_as_read';
      }
    | {
          type: 'notification_mark_as_unread';
      }
) &
    AnalyticsContextNotification &
    AnalyticsEventContext;

export type ScreenViewEvent =
    /** Page view in the application */
    {
        type: 'screen_view';
    } & AnalyticsContextScreen;

export interface OpenAPIAnalyticsCreateContext {
    triggerLocation:
        | 'home-empty-state-action'
        | 'openapi-screen-breadcrumb'
        | 'insert-block-dialog'
        | 'insert-content-source-dialog'
        | 'edit-content-source-dialog'
        | 'migrate-legacy-block-dialog'
        | 'contentkit-select'
        | 'create-organization';
}

export type OpenAPIAnalyticsEvent = (
    | ({
          type: 'openapi_spec_create';
          sourceType: 'file' | 'url';
      } & OpenAPIAnalyticsCreateContext)
    | ({
          type: 'openapi_spec_create_intent';
          trigger: 'menu-action' | 'empty-state-action';
      } & OpenAPIAnalyticsCreateContext)
    | {
          type: 'openapi_spec_edit';
          fromSourceType: 'file' | 'url';
          toSourceType: 'file' | 'url';
          specSlug: string;
      }
    | {
          type: 'openapi_spec_edit_intent';
          specSlug: string;
          triggerLocation: 'header';
          trigger: 'menu-action' | 'main-action';
      }
    | {
          type: 'openapi_spec_delete';
          specSlug: string;
      }
    | {
          type: 'openapi_spec_check_for_updates';
          specSlug: string;
      }
    | {
          type: 'openapi_spec_check_for_updates';
          specSlug: string;
      }
) &
    AnalyticsContextOrg;

export type TranslationAnalyticsIntent = 'space_creation' | 'computed_content';

export type TranslationAnalyticsEvent = (
    | {
          type: 'translation_language_settings_save';
          language: api.TranslationLanguage;
      }
    | {
          type: 'translation_settings_create';
      }
    | {
          type: 'translation_settings_rename';
      }
    | {
          type: 'translation_create';
          sourceLanguage: api.TranslationLanguage;
          targetLanguage: api.TranslationLanguage;
          intent: TranslationAnalyticsIntent;
      }
    | ({
          type: 'translation_estimate_price';
          totalPages: number;
          totalWords: number;
      } & AnalyticsContextSpace)
    | {
          type: 'translation_edit';
          sourceLanguage: api.TranslationLanguage;
          targetLanguage: api.TranslationLanguage;
      }
    | {
          type: 'translation_delete';
          sourceLanguage: api.TranslationLanguage;
          targetLanguage: api.TranslationLanguage;
      }
    | {
          type: 'translation_glossary_edit';
          inserts: number;
          updates: number;
          deletes: number;
      }
    | {
          type: 'translation_rerun';
          sourceLanguage: api.TranslationLanguage;
          targetLanguage: api.TranslationLanguage;
      }
) &
    AnalyticsContextOrg;

/**
 * Events are tracked on the client side.
 */
export type ClientAnalyticsEvent = (
    | CommonAnalyticsEvent
    | ScreenViewEvent
    | {
          type: 'close_legacy_github_deprecation_modal';
          user: string;
          organization: string;
      }
    | {
          type: 'crash';
          crashType: string;
          sentryEvent: string;
      }
    | {
          type: 'app_back_online';
          /** Time spent offline in seconds */
          offlineDuration: number;
      }
    | {
          type: 'app_version_update';
          previousVersion: string;
          currentVersion: string;
      }
    // Cookies
    | {
          type: 'cookies_set_consent';
          granted: boolean;
      }
    // Account
    | {
          type: 'auth_send_link';
      }
    | {
          type: 'auth_sign_in';
          method: AnalyticsAuthenticationMethod;
          mfa?: boolean;
      }
    | {
          type: 'auth_reauthenticate';
          method: AnalyticsAuthenticationMethod;
          mfa?: boolean;
      }
    | ({
          type: 'auth_sso_sign_in';
      } & AnalyticsContextOrg)
    | {
          type: 'auth_forgot_password';
      }
    | {
          type: 'auth_reset_password';
      }
    | {
          type: 'auth_update_password';
      }
    | {
          type: 'auth_view_sign_in_methods';
      }
    | {
          type: 'auth_update_email';
      }
    | {
          type: 'auth_send_email_verification';
      }
    | {
          type: 'auth_mfa_enroll';
      }
    | {
          type: 'auth_mfa_unenroll';
      }
    | {
          type: 'auth_sign_out';
      }
    | {
          type: 'account_edit_profile';
      }
    | {
          type: 'account_edit_hostname';
          hostname: string;
      }
    | {
          type: 'account_edit_default_content';
          contentType: 'space' | 'collection' | null;
      }
    | {
          type: 'account_delete';
      }
    // Feature flags
    | {
          type: 'feature_request_access_open';
          featureKey: string;
      }
    | {
          type: 'feature_request_access_submit';
          featureKey: string;
      }
    // Adaptive content segments
    | {
          type: 'adaptive_content_add_segment';
      }
    | {
          type: 'adaptive_content_edit_segment';
      }
    | {
          type: 'adaptive_content_delete_segment';
      }
    | {
          type: 'adaptive_content_duplicate_segment';
      }
    // User preferences
    | {
          type: 'account_edit_theme';
          theme: UserTheme;
      }
    | {
          type: 'account_edit_notification';
          notificationEnabled: boolean;
          notificationType: NotificationType;
          notificationSubscription: NotificationSubscription;
          notificationChannel: NotificationChannel;
      }
    // Onboarding
    | {
          type: 'organization_open_create';
          reason: OrganizationUpgradeReason;
      }
    // Other categories
    | OrganizationAnalyticsEvent
    | SiteAnalyticsEvent
    | SpaceAnalyticsEvent
    | CollectionAnalyticsEvent
    | PageAnalyticsEvent
    | TeamAnalyticsEvent
    | SpaceEditAnalyticsEvent
    | CommentAnalyticsEvent
    | ChangeRequestAnalyticsEvent
    | AllNotificationAnalyticsEvent
    | NotificationsAnalyticsEvent
    | PublicContentAnalyticsEvent
    | IntegrationAnalyticsEvent
    | TemplatesAnalyticsEvents
    | AIAnalyticsEvents
    | ChangeRequestAgentsAnalyticsEvents
    | SearchAnalyticsEvents
    | InsightsClientAnalyticsEvent
    | BrokenLinksAnalyticsEvent
    | IntegrationClientAnalyticsEvent
    | OpenAPIAnalyticsEvent
    | TranslationAnalyticsEvent
    | ImportClientAnalyticsEvent
) &
    AnalyticsEventContext;
